import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
// eslint-disable-next-line import/no-unresolved
import { MAIN_ICON_COLORS } from 'assets/components';
import { CARD_TYPE, mainCardTypes, orderCardFlowScreens } from '../constants';
import { CARD_NETWORKS, cardProviders } from '../../../constants';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import ModalBodyLayout from '../partials/MainBodyLayout';
import Icon from '../../../../../components/Icon';
import { ICONS } from '../../../../../components/Icon/constants';
import { Typography, Button } from '../../../../../components';
import { useUsEnv } from '../../../../../utilities/sharedHooks/useUsEnv';

const SelectMainCardType = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();

  const { state, openScreen, updateMainCardType } = useContext(OrderCardContext);

  const { isUsEnv } = useUsEnv();

  const mainCardType = state?.mainCardType;
  const cardProvidersWithTypesObj = state?.cardProvidersWithTypesObj;

  const [selectedOption, setSelectedOption] = useState(mainCardType ?? mainCardTypes.PHYSICAL);

  const CardImage = useMemo(() => {
    if (!state.cardProvider || !cardProvidersWithTypesObj) return null;

    if (
      state.cardProvider === cardProviders.GALILEO &&
      selectedOption === mainCardTypes.VIRTUAL &&
      cardProvidersWithTypesObj[state.cardProvider][selectedOption.toUpperCase()] ===
        CARD_NETWORKS.VISA
    ) {
      return 'galileo_visa_virtual';
    }

    if (
      state.cardProvider === cardProviders.GALILEO &&
      cardProvidersWithTypesObj[state.cardProvider][selectedOption.toUpperCase()] ===
        CARD_NETWORKS.VISA
    ) {
      return 'galileo_visa';
    }

    if (
      state.cardProvider === cardProviders.GALILEO &&
      selectedOption === mainCardTypes.VIRTUAL &&
      cardProvidersWithTypesObj[state.cardProvider][selectedOption.toUpperCase()] ===
        CARD_NETWORKS.MASTERCARD
    ) {
      return 'galileo_mastercard_virtual';
    }

    if (
      state.cardProvider === cardProviders.GALILEO &&
      cardProvidersWithTypesObj[state.cardProvider][selectedOption.toUpperCase()] ===
        CARD_NETWORKS.MASTERCARD
    ) {
      return 'galileo_mastercard';
    }

    if (selectedOption === mainCardTypes.VIRTUAL) {
      return 'mastercard_virtual';
    }
    return 'mastercard_physical';
  }, [state.cardProvider, selectedOption, cardProvidersWithTypesObj]);

  const notes = useMemo(
    () => [
      {
        id: 0,
        type: '',
        text: translate('card_order_select_main_type_note_1'),
        icon: <Icon icon={ICONS.OUTLINE_CARD} color={MAIN_ICON_COLORS.WHITE} />,
      },
      {
        id: 1,
        type: '',
        text: isUsEnv
          ? translate('card_order_select_main_type_note_2')
          : translate('card_order_select_main_type_note_us_2'),
        icon: <Icon icon={ICONS.LIGHT} color={MAIN_ICON_COLORS.WHITE} />,
      },
      {
        id: 2,
        type: '',
        text: isUsEnv
          ? translate('card_order_select_main_type_us_note_3')
          : translate('card_order_select_main_type_note_3'),
        icon: <Icon icon={ICONS.CARD_NOTE} color={MAIN_ICON_COLORS.WHITE} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isUsEnv]
  );

  const renderMainCardTypesTabs = () => (
    <div className="d-flex flex-row align-items-center mt-4 w-100 gap-3 flex-md-fill mb-md-3">
      <div
        className={`d-flex flex-column align-items-center py-3 border rounded-extra w-50 border-2 ${
          selectedOption === mainCardTypes.VIRTUAL
            ? 'bg-lighter border-primary-dimmed'
            : 'bg-light border-grey-lighter'
        }`}
        role="button"
        tabIndex="-1"
        onClick={() => {
          setSelectedOption(mainCardTypes.VIRTUAL);
          updateMainCardType(mainCardTypes.VIRTUAL);
        }}
        onKeyDown={() => {}}>
        <div style={{ height: '1.5rem', width: '1.5rem' }}>
          <Icon
            icon={ICONS.OUTLINE_CARD}
            color={
              selectedOption === mainCardTypes.VIRTUAL
                ? MAIN_ICON_COLORS.PRIMARY
                : MAIN_ICON_COLORS.WHITE
            }
          />
        </div>
        <Typography className="mt-2 text-dark" variant="small">
          {translate('card_order_virtual_card_option')}
        </Typography>
      </div>
      <div
        className={`d-flex flex-column align-items-center py-3 border rounded-extra w-50 border-2 ${
          selectedOption === mainCardTypes.PHYSICAL
            ? 'bg-lighter border-primary-dimmed'
            : 'bg-light border-grey-lighter'
        }`}
        role="button"
        tabIndex="-1"
        onClick={() => {
          updateMainCardType(mainCardTypes.PHYSICAL);
          setSelectedOption(mainCardTypes.PHYSICAL);
        }}
        onKeyDown={() => {}}>
        <div style={{ height: '1.5rem', width: '1.5rem' }}>
          <Icon
            icon={ICONS.OUTLINE_CARD}
            color={
              selectedOption === mainCardTypes.PHYSICAL
                ? MAIN_ICON_COLORS.PRIMARY
                : MAIN_ICON_COLORS.WHITE
            }
          />
        </div>
        <Typography className="mt-2 text-dark" variant="small">
          {translate('card_order_physical_card_option')}
        </Typography>
      </div>
    </div>
  );

  const handleOnConfirm = () => {
    if (selectedOption === mainCardTypes.VIRTUAL) {
      openScreen(orderCardFlowScreens.CONFIRM_VIRTUAL_DETAILS);
    } else {
      openScreen(orderCardFlowScreens.FREE_CARD_PROMO, {
        prevScreen: orderCardFlowScreens.SELECT_CARD_TYPE,
      });
    }
  };

  const renderFooter = () => (
    <div className="btn-container">
      <Button
        variant="primary"
        disabled={!selectedOption}
        onClick={handleOnConfirm}
        className="w-100">
        {translate('continue_btn_title')}
      </Button>
    </div>
  );

  const closeCardOrder = () => history.push('/dashboard/card');

  return (
    <ModalBodyLayout withBack onPrevClick={closeCardOrder}>
      <div className="pt-5 mb-3 px-3">
        <div
          className="d-flex flex-row justify-content-center"
          style={{
            height: '22rem',
            maxHeight: '22rem',
          }}>
          {CardImage === 'galileo_visa_virtual' && (
            <img
              className="mw-100 pb-5"
              src="./assets/images/card-galileo-visa-virtual.png"
              alt="promo"
              loading="lazy"
              rel="preload"
            />
          )}
          {CardImage === 'galileo_visa' && (
            <img
              className="mw-100 pb-5"
              src="./assets/images/card-galileo-visa.png"
              alt="promo"
              loading="lazy"
              rel="preload"
            />
          )}
          {CardImage === 'galileo_mastercard_virtual' && (
            <img
              className="mw-100 pb-5"
              src="./assets/images/card-galileo-mastercard-virtual.png"
              alt="promo"
              loading="lazy"
              rel="preload"
            />
          )}
          {CardImage === 'galileo_mastercard' && (
            <img
              className="mw-100 pb-5"
              src="./assets/images/card-galileo-mastercard.png"
              alt="promo"
              loading="lazy"
              rel="preload"
            />
          )}
          {CardImage === 'mastercard_virtual' && (
            <img
              rel="preload"
              loading="lazy"
              className="mw-100 pb-5"
              src="./assets/images/order-card-optimus-virtual.png"
              alt="promo"
            />
          )}
          {CardImage === 'mastercard_physical' && (
            <img
              rel="preload"
              className="mw-100 pb-5"
              loading="lazy"
              src="./assets/images/optimus_physical.png"
              alt="promo"
            />
          )}
        </div>
        {renderMainCardTypesTabs()}
        <div className="d-flex flex-column align-items-center mt-4 pt-3 pb-3">
          <Typography variant="xLargeSemi" className="text-dark">
            {translate(`card_order_${CARD_TYPE[selectedOption].state}`)}
          </Typography>
          <Typography variant="smallSemi" className="text-shadow pt-2">
            {translate(`card_order_${CARD_TYPE[selectedOption].note}`)}
          </Typography>
        </div>
        {notes.map((note) => (
          <div
            key={note.id}
            className="d-flex flex-row justify-content-start align-items-center py-2">
            <div
              className="d-flex justify-content-center align-items-center rounded-max bg-grey-lighter me-3"
              style={{ height: '3rem', width: '3rem' }}>
              <div style={{ height: '1.5rem', width: '1.5rem' }}>{note.icon}</div>
            </div>
            <Typography variant="base" className="text-dark">
              {note.text}
            </Typography>
          </div>
        ))}
        <div className="d-flex flex-row justify-content-start align-items-center pt-3">
          <img className="mw-100 me-3" src="./assets/images/apple-and-google.svg" alt="promo" />
          <Typography variant="base" className="text-dark">
            {translate('card_order_select_main_type_note_4')}
          </Typography>
        </div>
      </div>
      {renderFooter()}
    </ModalBodyLayout>
  );
};

export default SelectMainCardType;
